/**
 * Official state DMV / agency title & POA layouts.
 * Prevents numbered agency forms from falling through to Buyer's Order.
 */

import { formatMoney } from "./calc.js";

function esc(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function money(v) {
  return formatMoney(v);
}

function ownerName(ctx) {
  return String(ctx.buyer?.name || "").trim() || "—";
}

function ownerAddress(ctx) {
  const b = ctx.buyer || {};
  return [b.address, b.city, b.state, b.zip].filter(Boolean).join(", ") || "—";
}

function phone(ctx) {
  return String(ctx.buyer?.phone || "").trim() || "—";
}

function vin(ctx) {
  return String(ctx.vehicle?.vin || "").trim() || "________________";
}

function vehicleBits(ctx) {
  const v = ctx.vehicle || {};
  return {
    vin: vin(ctx),
    year: String(v.year || "").trim() || "____",
    make: String(v.make || "").trim() || "____",
    model: String(v.model || "").trim() || "____",
    body: String(v.body || "").trim() || "____",
    color: String(v.color || "").trim() || "____",
    mileage: String(v.mileage || "").trim() || "________",
    stock: String(v.stock || "").trim() || "—",
    label: String(v.label || "").trim() || "Vehicle",
  };
}

/**
 * South Carolina SCDMV Form 400 Rev. 12/2025 — Title and/or Registration Application.
 * Structured to match agency sections: VIN/owner/phone/odometer/lien/purchase/signatures.
 */
export function renderScForm400(ctx, helpers) {
  const { signatures, dealerLabel } = helpers;
  const v = vehicleBits(ctx);
  const dealer = dealerLabel(ctx);
  const today = new Date().toLocaleDateString("en-US");
  const formNo = String(ctx.form?.formNumber || "Form 400 Rev. 12/2025").trim();

  return `
    <div class="header official-dmv-header">
      <p class="brand-line">South Carolina Department of Motor Vehicles · Desking by EnvisionDMS.com</p>
      <h1>Title and/or Registration Application</h1>
      <p class="state-seal">SCDMV Form 400 · ${esc(formNo)} · dmv.sc.gov</p>
      <p class="meta">No strikeovers, erasures (VIN / Odometer), correction tape, or correction fluid. Applications: SCDMV branch offices or mail to SCDMV, 10311 Wilson Blvd., Blythewood, SC 29016-0038.</p>
    </div>

    <h2>Section 1 — Required for All Transactions</h2>
    <div class="checkline"><span class="checkbox"></span><span>TITLE</span>
      <span class="checkbox" style="margin-left:12px"></span><span>REGISTRATION NEW</span>
      <span class="checkbox" style="margin-left:12px"></span><span>PLATE TRANSFER</span>
      <span class="checkbox" style="margin-left:12px"></span><span>RECORD LIEN</span>
      <span class="checkbox" style="margin-left:12px"></span><span>DUPLICATE TITLE</span>
      <span class="checkbox" style="margin-left:12px"></span><span>LEASE</span></div>

    <h2>Vehicle Information</h2>
    <div class="grid2">
      <div class="field"><label>Vehicle Identification Number (VIN)</label><div class="val">${esc(v.vin)}</div></div>
      <div class="field"><label>Year</label><div class="val">${esc(v.year)}</div></div>
      <div class="field"><label>Make</label><div class="val">${esc(v.make)}</div></div>
      <div class="field"><label>Model</label><div class="val">${esc(v.model)}</div></div>
      <div class="field"><label>Body Style</label><div class="val">${esc(v.body)}</div></div>
      <div class="field"><label>Color</label><div class="val">${esc(v.color)}</div></div>
      <div class="field"><label>Empty Weight</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>Fuel</label><div class="val">☐ Gas &nbsp; ☐ Diesel &nbsp; ☐ Hybrid &nbsp; ☐ Electric</div></div>
    </div>

    <h2>Owner / Lessee Information</h2>
    <div class="grid2">
      <div class="field"><label>New Primary Owner/Lessee Complete Legal Name</label><div class="val">${esc(
        ownerName(ctx)
      )}</div></div>
      <div class="field"><label>Customer No., DL No., SSN, or FEIN</label><div class="val">${esc(
        ctx.buyer?.dl || ""
      )}&nbsp;</div></div>
      <div class="field"><label>Date of Birth</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>Phone Number</label><div class="val">${esc(phone(ctx))}</div></div>
      <div class="field"><label>Residence Address</label><div class="val">${esc(ownerAddress(ctx))}</div></div>
      <div class="field"><label>County</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>Mailing Address (if different)</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>Address Where Vehicle is Housed</label><div class="val">${esc(
        ownerAddress(ctx)
      )}</div></div>
    </div>
    <div class="field"><label>Co-Owner / Lessee Name (if any)</label><div class="val">&nbsp;</div></div>
    <div class="checkline"><span class="checkbox"></span><span>Shared ownership AND</span>
      <span class="checkbox" style="margin-left:12px"></span><span>Shared ownership OR</span></div>

    <h2>Section 2 — Title Transactions</h2>
    <h3 style="font-size:11px;margin:6px 0 4px;text-transform:uppercase;">Odometer Mileage</h3>
    <p class="legal">Federal and state law require mileage disclosure when transferring ownership. False statements may result in fines and/or imprisonment.</p>
    <div class="box"><strong>I state that the odometer now reads (miles, no tenths)</strong><span class="amount">${esc(
      v.mileage
    )}</span></div>
    <div class="checkline"><span class="checkbox"></span><span>Actual mileage (to the best of my knowledge)</span></div>
    <div class="checkline"><span class="checkbox"></span><span>EXEMPT</span></div>
    <div class="checkline"><span class="checkbox"></span><span>Mileage in excess of mechanical limits</span></div>
    <div class="checkline"><span class="checkbox"></span><span>Not the actual mileage — WARNING ODOMETER DISCREPANCY</span></div>

    <h3 style="font-size:11px;margin:8px 0 4px;text-transform:uppercase;">Lien Information</h3>
    <div class="grid2">
      <div class="field"><label>First Lienholder Name</label><div class="val">As on retail contract / ELT</div></div>
      <div class="field"><label>Date of Lien</label><div class="val">${esc(today)}</div></div>
      <div class="field"><label>Lienholder Phone</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>Lienholder Mailing Address</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>Customer No. or FEIN (ELT)</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>Second Lienholder (if any)</label><div class="val">&nbsp;</div></div>
    </div>
    <div class="grid2">
      <div class="field"><label>Prior Title State</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>Prior Title Number</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>Date First Operated in SC</label><div class="val">${esc(today)}</div></div>
      <div class="field"><label>Stock #</label><div class="val">${esc(v.stock)}</div></div>
    </div>

    <h3 style="font-size:11px;margin:8px 0 4px;text-transform:uppercase;">Purchase Information</h3>
    <div class="grid2">
      <div class="field"><label>Seller or Dealer Name</label><div class="val">${esc(dealer)}</div></div>
      <div class="field"><label>Dealer No. / Sales Tax No.</label><div class="val">${esc(
        ctx.dealer?.license || ""
      )}&nbsp;</div></div>
      <div class="field"><label>Dealer Address</label><div class="val">${esc(
        ctx.dealer?.address || ""
      )}&nbsp;</div></div>
      <div class="field"><label>Date of Purchase</label><div class="val">${esc(today)}</div></div>
      <div class="field"><label>Sales Price</label><div class="val">${money(ctx.deal?.price)}</div></div>
      <div class="field"><label>Trade-In Amount</label><div class="val">${money(
        ctx.deal?.tradeAllowance
      )}</div></div>
      <div class="field"><label>Trade-In VIN</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>New or Used</label><div class="val">☐ New &nbsp; ☐ Used</div></div>
    </div>

    <h2>Section 3 — Registration (if applicable)</h2>
    <div class="checkline"><span class="checkbox"></span><span>NEW PLATE</span>
      <span class="checkbox" style="margin-left:12px"></span><span>TRANSFER PLATE</span>
      <span class="checkbox" style="margin-left:12px"></span><span>EXCHANGE</span></div>
    <div class="grid2">
      <div class="field"><label>Plate Number to Transfer</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>Gross Vehicle Weight (GVW)</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>Insurance Company (I declare vehicle is insured with)</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>SSN or FEIN (if GVW &gt; 26,000 or bus carrier)</label><div class="val">&nbsp;</div></div>
    </div>

    <h2>Section 4 — Certification &amp; Signatures</h2>
    <p class="legal">I DECLARE THAT I AM THE OWNER OF THIS VEHICLE. I REQUEST THAT A SOUTH CAROLINA CERTIFICATE OF TITLE AND/OR REGISTRATION BE ISSUED. THE VEHICLE IS SUBJECT TO THE LIENS NAMED AND NO OTHERS. UNDER PENALTY OF PERJURY, I CERTIFY ALL INFORMATION PROVIDED IS TRUE AND CURRENT. Signature of owner(s) may be signed in ink by owner or authorized agent (attach power of attorney if applicable). Electronic or digital signatures are acceptable.</p>
    <div class="checkline"><span class="checkbox"></span><span>Donate Life SC — Yes, I wish to donate $5.00 (or other amount): $______</span></div>
    <p class="legal">Late registration penalties may apply if not registered within 45 days of purchase or first operation in South Carolina (46–60 days $10; 61–75 $25; 76–135 $50; over 135 $75).</p>
    <p class="meta">Merged from Desking by EnvisionDMS.com worksheet ${esc(
      ctx.deal?.id || "—"
    )} · Official SCDMV Form 400 layout (Rev. 12/2025). Confirm current SCDMV instructions at dmv.sc.gov before filing.</p>
    ${signatures(ctx)}
  `;
}

/**
 * Mississippi DOR Form 78-003 — Power of Attorney to Transfer Motor Vehicle (optional POA).
 * Grantor / VIN / odometer / attorney-in-fact merge from deal context.
 */
export function renderMs78003(ctx, helpers) {
  const { signatures, dealerLabel } = helpers;
  const v = vehicleBits(ctx);
  const grantor = ownerName(ctx);
  const attorney = dealerLabel(ctx);
  const formNo = String(ctx.form?.formNumber || "78-003-21-8-1-000 Rev. 06/21").trim();
  const today = new Date();

  return `
    <div class="header official-dmv-header">
      <p class="brand-line">Mississippi Department of Revenue — Motor Vehicle Services</p>
      <h1>Power of Attorney to Transfer Motor Vehicle</h1>
      <p class="state-seal">Form 78-003 · ${esc(formNo)} · P.O. Box 1383, Jackson, MS 39215-1383</p>
      <p class="meta">This document shall pass with the Certificate of Title and be included as a support document to the application for new Certificate of Title and/or Application for Replacement Certificate of Title.</p>
    </div>

    <p class="legal">Form 78-003-${esc("21-8-1-000")} &nbsp;|&nbsp; THE STATE OF MISSISSIPPI</p>
    <div class="field"><label>County of</label><div class="val">&nbsp;</div></div>

    <h2>Grantor (Owner)</h2>
    <p class="legal">KNOW ALL MEN BY THESE PRESENT: THAT I,</p>
    <div class="field"><label>Grantor name (same form as contained in Certificate of Title)</label><div class="val">${esc(
      grantor
    )}</div></div>
    <div class="field"><label>Grantor address</label><div class="val">${esc(ownerAddress(ctx))}</div></div>
    <div class="field"><label>Phone</label><div class="val">${esc(phone(ctx))}</div></div>
    <p class="legal">of the County of ______________ and the STATE OF MISSISSIPPI, owner of the following described motor vehicle, have made, constituted and appointed and by these presents do make, constitute and appoint:</p>

    <h2>Attorney-in-Fact</h2>
    <div class="field"><label>Attorney-in-fact (if a business, include authorized representative)</label><div class="val">${esc(
      attorney
    )}</div></div>
    <div class="field"><label>Of the County of</label><div class="val">&nbsp;</div></div>
    <p class="legal">and the STATE OF MISSISSIPPI, my true and lawful attorney, for me and in my name, place and stead to sell, transfer and assign the motor vehicle described as follows, to wit:</p>

    <h2>Vehicle</h2>
    <div class="grid2">
      <div class="field"><label>Year</label><div class="val">${esc(v.year)}</div></div>
      <div class="field"><label>Make</label><div class="val">${esc(v.make)}</div></div>
      <div class="field"><label>Body Type</label><div class="val">${esc(v.body)}</div></div>
      <div class="field"><label>V.I.N.</label><div class="val">${esc(v.vin)}</div></div>
      <div class="field"><label>Title Number</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>Model / Color</label><div class="val">${esc(v.model)} / ${esc(
        v.color
      )}</div></div>
    </div>
    <p class="legal">giving and granting unto my said attorney full power and authority to do and perform all and every act requisite and necessary to transfer and assign the legal title to said motor vehicle to anyone whomever, as may be designated by said attorney.</p>

    <h2>Odometer Disclosure</h2>
    <p class="legal">FEDERAL and STATE LAW require that you state the mileage in connection with the transfer of ownership. Failure to complete or providing a false statement may result in fines and/or imprisonment.</p>
    <p class="legal">I certify to the best of my knowledge that the odometer reading is the actual mileage of the vehicle unless one of the following statements is checked.</p>
    <div class="box"><strong>Odometer Reading (No Tenths)</strong><span class="amount">${esc(
      v.mileage
    )}</span></div>
    <div class="checkline"><span class="checkbox"></span><span>1. I hereby certify that the mileage stated is in excess of the mechanical limits of the odometer.</span></div>
    <div class="checkline"><span class="checkbox"></span><span>2. The odometer reading is not actual mileage. WARNING — ODOMETER DISCREPANCY</span></div>

    <h2>Execution</h2>
    <p class="legal">IN WITNESS WHEREOF I have hereunto set my hand this ________ day of ______________, 20${String(
      today.getFullYear()
    ).slice(2)}.</p>
    <div class="field"><label>Printed Name of Grantor (if title is in company name, include title)</label><div class="val">${esc(
      grantor
    )}</div></div>
    <p class="legal">Before me, the undersigned authority, on this day personally appeared ________________________________ known to me to be the person whose name is subscribed to the above foregoing instrument, and acknowledged to me that he/she executed the same for the purposes and consideration therein expressed.</p>
    <div class="grid2">
      <div class="field"><label>Notary Public</label><div class="val">&nbsp;</div></div>
      <div class="field"><label>Commission / Seal</label><div class="val">&nbsp;</div></div>
    </div>
    <p class="meta">Optional Mississippi Form 78-003 POA · Merged grantor/VIN/odometer/attorney-in-fact from Desking by EnvisionDMS.com worksheet ${esc(
      ctx.deal?.id || "—"
    )}. Confirm current MDOR instructions before filing.</p>
    ${signatures(ctx)}
  `;
}

/**
 * Generic structured official title / registration application for other state DMS forms.
 */
export function renderOfficialDmv(ctx, helpers) {
  const { dealerHeader, buyerBox, vehicleBox, signatures, stateAgency, dealerLabel } = helpers;
  const v = vehicleBits(ctx);
  const formNo = String(ctx.form?.formNumber || "").trim();
  const title = ctx.form?.name || "Official Title / Registration Application";
  const agency = stateAgency(ctx.stateCode);
  const today = new Date().toLocaleDateString("en-US");

  return `${dealerHeader(ctx, title)}
    <p class="meta">Official agency form layout for ${esc(ctx.stateName)} · Submit to: ${esc(
      agency
    )}${formNo ? ` · Form No. ${esc(formNo)}` : ""}</p>
    <p class="legal">This is a structured Envision Forms Library worksheet aligned to ${esc(
      ctx.stateName
    )} motor-vehicle titling practice. Where your state publishes a mandatory numbered DMV blank, attach or complete the current agency PDF/portal form; values below are merged for deal-jacket use and filing preparation.</p>

    <h2>1. Transaction</h2>
    <div class="checkline"><span class="checkbox"></span><span>Original title</span>
      <span class="checkbox" style="margin-left:10px"></span><span>Transfer</span>
      <span class="checkbox" style="margin-left:10px"></span><span>Registration / plates</span>
      <span class="checkbox" style="margin-left:10px"></span><span>Duplicate / replacement</span>
      <span class="checkbox" style="margin-left:10px"></span><span>Add / release lien</span></div>

    <h2>2. Vehicle (VIN · Year · Make · Model)</h2>
    <div class="grid2">
      <div class="field"><label>VIN</label><div class="val">${esc(v.vin)}</div></div>
      <div class="field"><label>Year / Make / Model</label><div class="val">${esc(v.year)} ${esc(
        v.make
      )} ${esc(v.model)}</div></div>
      <div class="field"><label>Body / Color</label><div class="val">${esc(v.body)} / ${esc(
        v.color
      )}</div></div>
      <div class="field"><label>Stock #</label><div class="val">${esc(v.stock)}</div></div>
    </div>

    <h2>3. Owner / Applicant</h2>
    <div class="grid2">${buyerBox(ctx, "Primary Owner / Applicant")}
      <div class="box"><strong>Phone</strong>${esc(phone(ctx))}<br><strong>DL / ID</strong> ${esc(
        ctx.buyer?.dl || "—"
      )}<br><strong>Email</strong> ${esc(ctx.buyer?.email || "—")}</div>
    </div>

    <h2>4. Odometer</h2>
    <div class="box"><strong>Odometer (miles, no tenths)</strong><span class="amount">${esc(
      v.mileage
    )}</span></div>
    <div class="checkline"><span class="checkbox"></span><span>Actual mileage</span></div>
    <div class="checkline"><span class="checkbox"></span><span>Exceeds mechanical limits</span></div>
    <div class="checkline"><span class="checkbox"></span><span>Not actual mileage — WARNING DISCREPANCY</span></div>

    <h2>5. Lien · Purchase</h2>
    <div class="grid2">
      <div class="field"><label>Lienholder</label><div class="val">As shown on retail / finance contract</div></div>
      <div class="field"><label>Date of Lien / Sale</label><div class="val">${esc(today)}</div></div>
      <div class="field"><label>Seller / Dealer</label><div class="val">${esc(dealerLabel(ctx))}</div></div>
      <div class="field"><label>Dealer License #</label><div class="val">${esc(
        ctx.dealer?.license || "—"
      )}</div></div>
      <div class="field"><label>Purchase Price</label><div class="val">${money(ctx.deal?.price)}</div></div>
      <div class="field"><label>Trade Allowance</label><div class="val">${money(
        ctx.deal?.tradeAllowance
      )}</div></div>
    </div>

    <h2>6. Certification</h2>
    <p class="legal">Applicant certifies under ${esc(
      ctx.stateName
    )} law that the information is true and the vehicle is subject only to the liens disclosed. Filing must follow current ${esc(
      agency
    )} instructions, fees, and supporting documents (POA, odometer statement, insurance, inspection as required).</p>
    ${signatures(ctx)}`;
}

/** Map catalog formType / id / formNumber → official renderer key. */
export function resolveOfficialDmvType(form = {}) {
  const type = String(form.formType || "").toLowerCase();
  const id = String(form.id || "").toLowerCase();
  const num = String(form.formNumber || "").toLowerCase();
  const name = String(form.name || "").toLowerCase();
  const blob = `${type} ${id} ${num} ${name}`;

  if (
    type === "dmv-form-400" ||
    blob.includes("dmv-form-400") ||
    blob.includes("form 400") ||
    (blob.includes("form-400") && blob.includes("sc"))
  ) {
    return "dmv-form-400";
  }
  if (
    type === "dmv-78-003" ||
    blob.includes("78-003") ||
    (blob.includes("power of attorney to transfer") && blob.includes("mississippi"))
  ) {
    return "dmv-78-003";
  }
  if (
    type.startsWith("dmv-") ||
    type === "tx-vtr-130u" ||
    type === "title-reassignment" ||
    type === "vin-verification" ||
    type === "marine-title-application" ||
    type === "marine-trailer-title" ||
    type === "marine-registration" ||
    type === "ca-reg-262"
  ) {
    return "official-dmv";
  }
  if (String(form.category || "").toUpperCase() === "DMV") {
    return "official-dmv";
  }
  return null;
}
